/**
 * Pipeline Progress Component
 * 
 * Shows the 6-step grading pipeline progress
 */

import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';

export interface PipelineStatus {
  id: string;
  assignmentId: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  percentComplete: number;
  currentStepName: string;
  questionsDetected: number | null;
  questionsGraded: number | null;
  questionsFlagged: number | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

interface PipelineProgressProps {
  pipelineStatus: PipelineStatus | null;
  loading?: boolean;
  onRetry?: () => void;
}

const PIPELINE_STEPS = [
  { key: 'identifying_questions', label: '識別題目區域', description: '使用 AI 視覺模型識別試卷中的問題區域' },
  { key: 'transcribing', label: '轉錄手寫內容', description: '將手寫答案轉換為文字' },
  { key: 'loading_rubric', label: '載入評分標準', description: '載入並解析評分準則' },
  { key: 'grading', label: '評分中', description: '根據評分標準評估每個答案' },
  { key: 'verifying', label: '驗證結果', description: '檢查評分結果是否合理' },
  { key: 'completed', label: '完成', description: '評分完成' },
];

function getStepIndex(status: string): number {
  const mapping: Record<string, number> = {
    pending: -1,
    identifying_questions: 0,
    awaiting_paper_map_review: 0,  // Still on step 0, waiting for confirmation
    transcribing: 1,
    awaiting_transcript_review: 1, // Still on step 1
    loading_rubric: 2,
    grading: 3,
    verifying: 4,
    awaiting_grading_review: 5,    // Completed, awaiting review
    completed: 6,                   // Beyond last step = all completed
    failed: -2,
  };
  return mapping[status] ?? -1;
}

export function PipelineProgress({
  pipelineStatus,
  loading = false,
  onRetry,
}: PipelineProgressProps) {
  if (loading || !pipelineStatus) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>正在載入批改進度...</Typography>
      </Paper>
    );
  }

  const currentStepIndex = getStepIndex(pipelineStatus.status);
  const isFailed = pipelineStatus.status === 'failed';
  const isCompleted = pipelineStatus.status === 'completed' || 
                      pipelineStatus.status === 'awaiting_review' || 
                      pipelineStatus.status === 'awaiting_grading_review';

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        批改進度
      </Typography>

      {isFailed && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                重試
              </Button>
            )
          }
        >
          批改失敗：{pipelineStatus.errorMessage || '未知錯誤'}
        </Alert>
      )}

      {isCompleted && (pipelineStatus.status === 'awaiting_review' || pipelineStatus.status === 'awaiting_grading_review') && (
        <Alert severity="info" sx={{ mb: 3 }}>
          批改完成，需要人工審核 {pipelineStatus.questionsFlagged || 0} 個題目
        </Alert>
      )}

      {isCompleted && pipelineStatus.status === 'completed' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          批改已完成！共識別 {pipelineStatus.questionsDetected} 個題目
        </Alert>
      )}

      <Stepper 
        activeStep={isCompleted ? PIPELINE_STEPS.length : currentStepIndex} 
        alternativeLabel
        sx={{ mb: 3 }}
      >
        {PIPELINE_STEPS.map((step, index) => {
          // When completed, all steps are complete
          const isStepComplete = isCompleted || index < currentStepIndex;
          const isActive = !isCompleted && index === currentStepIndex;
          const isError = isFailed && index === currentStepIndex;

          return (
            <Step key={step.key} completed={isStepComplete}>
              <StepLabel
                error={isError}
                StepIconComponent={() => {
                  if (isError) return <ErrorIcon color="error" />;
                  if (isStepComplete) return <CheckIcon color="success" />;
                  if (isActive) return <CircularProgress size={24} />;
                  return <PendingIcon color="disabled" />;
                }}
              >
                <Typography 
                  variant="caption" 
                  fontWeight={isActive ? 'bold' : 'normal'}
                  color={isActive ? 'primary' : 'text.secondary'}
                >
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Progress details */}
      <Box sx={{ textAlign: 'center' }}>
        {!isFailed && !isCompleted && (
          <>
            <CircularProgress 
              variant="determinate" 
              value={pipelineStatus.percentComplete} 
              size={80}
              sx={{ mb: 2 }}
            />
            <Typography variant="h6">
              {pipelineStatus.currentStepName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pipelineStatus.percentComplete}% 完成
            </Typography>
          </>
        )}

        {/* Stats */}
        {(pipelineStatus.questionsDetected || pipelineStatus.questionsGraded) && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 4 }}>
            {pipelineStatus.questionsDetected && (
              <Box>
                <Typography variant="h4" color="primary">
                  {pipelineStatus.questionsDetected}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  題目識別
                </Typography>
              </Box>
            )}
            {pipelineStatus.questionsGraded && (
              <Box>
                <Typography variant="h4" color="success.main">
                  {pipelineStatus.questionsGraded}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  已評分
                </Typography>
              </Box>
            )}
            {pipelineStatus.questionsFlagged !== null && pipelineStatus.questionsFlagged > 0 && (
              <Box>
                <Typography variant="h4" color="warning.main">
                  {pipelineStatus.questionsFlagged}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  需審核
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

